import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { supabase } from '../../../lib/supabaseClient';

const makeDataUrl = (b64, mime) => `data:${mime || 'image/png'};base64,${b64}`;

const dataUrlToFile = (dataUrl, filename) => {
  const parts = String(dataUrl || '').split(',');
  const header = parts[0] || '';
  const b64 = parts[1] || '';
  const mimeMatch = header.match(/data:(.*?);base64/);
  const mime = mimeMatch ? mimeMatch[1] : 'image/png';
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  const blob = new Blob([bytes], { type: mime });
  return new File([blob], filename || 'ai-challenge-cover.png', { type: mime });
};

const AIChallengeRecommendWizard = ({
  open,
  onClose,
  projectTitle,
  projectDescription,
  onSelect,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isGeneratingCover, setIsGeneratingCover] = useState(false);
  const [error, setError] = useState('');
  const [suggestions, setSuggestions] = useState([]);

  const canRequest = useMemo(
    () => !!projectTitle?.trim() && !!projectDescription?.trim(),
    [projectTitle, projectDescription]
  );

  const loadSuggestions = useCallback(async () => {
    if (!canRequest) return;

    setIsLoading(true);
    setError('');

    try {
      const { data, error: fnError } = await supabase.functions.invoke('ai-challenge-suggest', {
        body: { title: projectTitle, description: projectDescription },
      });

      if (fnError) {
        setError(fnError?.message || '챌린지 추천에 실패했습니다');
        return;
      }

      const list = Array.isArray(data?.suggestions) ? data.suggestions : [];
      if (list.length === 0) {
        setError('추천 결과가 비어 있습니다');
        return;
      }

      setSuggestions(list.slice(0, 10));
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setIsLoading(false);
    }
  }, [canRequest, projectTitle, projectDescription]);

  useEffect(() => {
    if (!open) return;
    setError('');
    setSuggestions([]);
    if (!canRequest) return;
    loadSuggestions();
  }, [open, canRequest, loadSuggestions]);

  const handleSelect = useCallback(
    async (item, index) => {
      if (!item) return;

      setIsGeneratingCover(true);
      setError('');

      try {
        const prompt = String(item?.imagePrompt || '').trim();
        if (!prompt) {
          setError('이미지 프롬프트가 비어 있습니다');
          return;
        }

        const { data, error: fnError } = await supabase.functions.invoke('ai-project-cover-generate', {
          body: { prompt },
        });

        if (fnError) {
          setError(fnError?.message || '챌린지 이미지 생성에 실패했습니다');
          return;
        }

        if (!data?.b64) {
          setError('이미지 데이터가 비어 있습니다');
          return;
        }

        const mime = data?.mime || 'image/png';
        const dataUrl = makeDataUrl(data.b64, mime);
        const file = dataUrlToFile(dataUrl, `ai-challenge-${index + 1}.png`);

        onSelect?.({
          ...item,
          coverImageDataUrl: dataUrl,
          coverImageFile: file,
        });

        onClose?.();
      } catch (e) {
        setError(String(e?.message || e));
      } finally {
        setIsGeneratingCover(false);
      }
    },
    [onClose, onSelect]
  );

  if (!open) return null;

  const busyLabel = isGeneratingCover ? '챌린지 커버 이미지를 생성 중입니다…' : 'AI 챌린지 추천을 불러오는 중입니다…';

  return (
    <div
      className="fixed inset-0 z-dialog bg-background/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={() => (isLoading || isGeneratingCover ? null : onClose?.())}
    >
      <div
        className="relative bg-card rounded-lg shadow-gaming-lg w-full max-w-3xl border border-primary/30 max-h-[90vh] h-[90vh] md:h-auto flex flex-col"
        onClick={(e) => e?.stopPropagation()}
      >
        {(isLoading || isGeneratingCover) && (
          <div className="absolute inset-0 z-20 bg-background/60 backdrop-blur-sm flex items-center justify-center">
            <div className="bg-card rounded-lg border border-border shadow-gaming-md px-5 py-4 max-w-sm w-full mx-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-md bg-primary/15 flex items-center justify-center flex-shrink-0">
                  <Icon name="Loader2" size={20} className="animate-spin" color="var(--color-primary)" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-heading font-semibold text-foreground">AI 처리 중</div>
                  <div className="mt-1 text-xs text-muted-foreground caption whitespace-pre-line">{busyLabel}</div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="sticky top-0 z-10 bg-card p-4 md:p-6 border-b border-border flex items-center justify-between">
          <div>
            <div className="text-lg md:text-xl font-heading font-semibold text-foreground">AI 챌린지 추천</div>
            <div className="text-xs md:text-sm text-muted-foreground caption mt-1">
              목표 제목/설명을 기반으로 작은 챌린지를 추천합니다
            </div>
          </div>
          <Button variant="outline" onClick={onClose} disabled={isLoading || isGeneratingCover}>
            닫기
          </Button>
        </div>

        <div className="p-4 md:p-6 flex-1 overflow-y-auto">
          {!canRequest && (
            <div className="flex items-start space-x-2 p-3 bg-warning/10 rounded-md border border-warning/20">
              <Icon name="AlertTriangle" size={16} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
              <p className="text-xs md:text-sm text-warning">목표 제목과 목표 설명을 먼저 입력해주세요.</p>
            </div>
          )}

          {error ? (
            <div className="mt-4 flex items-start space-x-2 p-3 bg-destructive/10 rounded-md border border-destructive/20">
              <Icon name="AlertCircle" size={16} color="var(--color-destructive)" className="flex-shrink-0 mt-0.5" />
              <p className="text-xs md:text-sm text-destructive">{error}</p>
            </div>
          ) : null}

          <div className="mt-4 flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              {isLoading ? '추천 생성 중…' : `추천 ${suggestions?.length || 0}개`}
            </div>
            <Button
              variant="outline"
              iconName="RefreshCcw"
              iconPosition="left"
              onClick={loadSuggestions}
              disabled={!canRequest || isLoading || isGeneratingCover}
            >
              다시 추천
            </Button>
          </div>

          <div className="mt-4 space-y-4">
            {suggestions.map((s, idx) => (
              <div key={`${idx}-${s?.title || 'item'}`} className="rounded-lg border border-border bg-background p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-base font-heading font-semibold text-foreground line-clamp-2">
                      {s?.title || `추천 ${idx + 1}`}
                    </div>
                    <div className="mt-2 text-sm text-muted-foreground whitespace-pre-line">{s?.description || ''}</div>

                    {Array.isArray(s?.tags) && s.tags.length > 0 ? (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {s.tags.slice(0, 10).map((t) => (
                          <span
                            key={t}
                            className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-muted text-muted-foreground border border-border"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    ) : null}

                    <div className="mt-3 text-xs text-muted-foreground">
                      목표 설정: <span className="font-medium">{String(s?.goalType || '')}</span>
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-2">
                    <Button
                      variant="default"
                      iconName="Check"
                      iconPosition="left"
                      onClick={() => handleSelect(s, idx)}
                      loading={isGeneratingCover}
                      disabled={isLoading || isGeneratingCover}
                    >
                      선택
                    </Button>
                  </div>
                </div>

                {s?.imagePrompt ? (
                  <details className="mt-3">
                    <summary className="text-xs text-muted-foreground cursor-pointer">이미지 프롬프트 보기</summary>
                    <div className="mt-2 text-xs text-muted-foreground whitespace-pre-line">{s.imagePrompt}</div>
                  </details>
                ) : null}
              </div>
            ))}
          </div>

          {isGeneratingCover ? (
            <div className="mt-4 rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
              선택한 챌린지 커버 이미지를 생성 중입니다…
            </div>
          ) : null}
        </div>

        <div className="sticky bottom-0 z-10 bg-card p-4 md:p-6 border-t border-border">
          <div className="flex items-center justify-end">
            <Button variant="outline" onClick={onClose} disabled={isLoading || isGeneratingCover}>
              닫기
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIChallengeRecommendWizard;
